import React, { useEffect, useState, useCallback } from "react";
import { Spin, Button, Typography, Empty, Tag, Flex, Input } from "antd";
import {
  AppstoreOutlined,
  FolderOpenOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { DidacticMaterialsAPI, VirtualScenes } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLocaleStore } from "../../../../store/localeStore";
import { SalasItemSalas } from "../SalasItem_Salas";
import { getCategoryColor, getCategoryIcon } from "../categoryMeta";
import { NEW_SALAS } from "../../../../ui/featureFlags";
import { RutasDidacticasForm } from "../../RutasDidacticasComp";
import { BrandThemeProvider } from "../../../../ui";

import styles from "./SalasList.module.scss";
import "./SalasList.scss";

// Fondo de tinte del icono a partir del color de categoria (hex + alpha).
const tint = (hex) => `${hex}1a`;

const { Text } = Typography;

const didacticMaterialsController = new DidacticMaterialsAPI();
const virtualScenesController = new VirtualScenes();

const CATEGORY_MAP = {
  "CIENCIAS HUMANAS": "e-button-humanas",
  "CIENCIAS NATURALES": "e-button-naturales",
  HISTORIA: "e-button-historia",
  MATEMÁTICAS: "e-button-matematicas",
  ARTE: "e-button-arte",
  "VIDA SALUDABLE": "e-button-vida-saludable",
  OTROS: "e-button-otros",
};

export function SalasList({
  classNumber,
  teacher,
  currentPage,
  onPageChange,
  onTotalPagesChange,
  reload,
  onReload,
}) {
  const [categories, setCategories] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const { t } = useTranslation("scenes");
  const otherLabel = t("catalog.myScenes");

  const [selectedCategory, setSelectedCategory] = useState(
    localStorage.getItem("selectedCategory") || null,
  );
  const [selectedSubcategory, setSelectedSubcategory] = useState(
    localStorage.getItem("selectedSubcategory") || null,
  );

  const [showRutasForm, setShowRutasForm] = useState(false);
  const [selectedScene, setSelectedScene] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const { accessToken, user } = useAuth();
  const locale = useLocaleStore((s) => s.locale);

  const getCategoryClass = (name) => CATEGORY_MAP[name] || "";

  const saveFiltersToLocalStorage = (category, subcategory) => {
    if (category !== undefined) {
      setSelectedCategory(category);
      localStorage.setItem("selectedCategory", category || "");
    }
    if (subcategory !== undefined) {
      setSelectedSubcategory(subcategory);
      localStorage.setItem("selectedSubcategory", subcategory || "");
    }
  };
  const SHOW_NEWEST_FIRST = true;

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setDataLoaded(false);
    try {
      const response = await didacticMaterialsController.getMaterialsByRegion(
        accessToken,
        "Europe",
      );
      setCategories(response.data?.categories || []);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error al cargar categorías:", error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, locale]); // locale triggers re-fetch on language switch via apiFetch

  const fetchScenes = useCallback(
    async (page = 1) => {
      setLoading(true);
      setDataLoaded(false);
      try {
        const response = await virtualScenesController.getVirtualScenesMini(
          { page, limit: 300 },
          accessToken,
        );

        let allScenes = response.miniatureData || [];

        if (selectedCategory === "OTROS") {
          allScenes = allScenes.filter(
            (s) => s.account === "creator" && s.userId === user.user._id,
          );
        } else {
          allScenes = allScenes.filter((s) => s.account !== "creator");

          if (selectedCategory) {
            allScenes = allScenes.filter((s) =>
              s.category.includes(selectedCategory),
            );
          }
          if (selectedSubcategory) {
            allScenes = allScenes.filter((s) =>
              s.subcategory.includes(selectedSubcategory),
            );
          }
        }
        // --- NUEVA LÓGICA DE INVERSIÓN ---
        if (SHOW_NEWEST_FIRST) {
          // Usamos reverse() para invertir el orden (del final hacia delante)
          // Se asume que el backend las envía por fecha de creación ascendente (viejas -> nuevas)
          allScenes = [...allScenes].reverse();
        }
        setScenes(allScenes);
        onTotalPagesChange(response.totalPages || 1);

        await Promise.all(
          allScenes.map(
            (scene) =>
              new Promise((resolve) => {
                const img = new Image();
                img.src = scene.bannertopUrl;
                img.onload = resolve;
                img.onerror = resolve;
              }),
          ),
        );

        setDataLoaded(true);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error al cargar escenas:", error);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      accessToken,
      onTotalPagesChange,
      selectedCategory,
      selectedSubcategory,
      user.user._id,
      SHOW_NEWEST_FIRST,
      locale, // locale triggers re-fetch on language switch via apiFetch
    ],
  );

  useEffect(() => {
    fetchCategories();
    fetchScenes(currentPage);
  }, [fetchCategories, fetchScenes, currentPage, reload]);

  const handleAllScenes = () => {
    saveFiltersToLocalStorage(null, null);
    fetchScenes(1);
    onPageChange(1);
  };

  const handleSelect = (category = null, subcategory = null) => {
    saveFiltersToLocalStorage(category, subcategory);
    fetchScenes(1);
    onPageChange(1);
  };

  const handleClearSubcategory = () => {
    saveFiltersToLocalStorage(selectedCategory, null);
    fetchScenes(1);
    onPageChange(1);
  };

  const handleUploadScene = (scene) => {
    setSelectedScene(scene);
    setShowRutasForm(true);
  };

  const handleCloseRutasForm = () => {
    setShowRutasForm(false);
    setSelectedScene(null);
  };

  const selectedCategoryObj = categories.find(
    (c) => c.nameRaw === selectedCategory,
  );

  // Buscador en tiempo real: filtra por titulo de la escena en cliente, sobre
  // las escenas ya cargadas de la categoria activa.
  const searchQuery = searchTerm.trim().toLowerCase();
  const visibleScenes = searchQuery
    ? scenes.filter((s) => (s.title || "").toLowerCase().includes(searchQuery))
    : scenes;

  return (
    <div className={`salas-list ${styles.wrap}`}>
      {loading || !dataLoaded ? (
        <div className={styles.loading}>
          <Spin size="large" />
          <p style={{ marginTop: 12, color: "#6b7280" }}>
            {t("catalog.loadingCatalog")}
          </p>
        </div>
      ) : (
        <div className={`ui-materials ${styles.materials}`}>
          {NEW_SALAS ? (
            <>
              {/* ===== Categorias (una fila) + buscador debajo ===== */}
              <div className={styles.tilesBar}>
                <div className={styles.tilesRow}>
                  <div
                    className={`${styles.tile} ${!selectedCategory ? styles.tileSelected : ""}`}
                    role="button"
                    tabIndex={0}
                    onClick={handleAllScenes}
                  >
                    <div
                      className={styles.tileIconWrap}
                      style={{ background: "rgba(100,116,139,.12)" }}
                    >
                      <AppstoreOutlined style={{ color: "#64748b" }} />
                    </div>
                    <span className={styles.tileLabel}>
                      {t("catalog.allButton")}
                    </span>
                    <span
                      className={styles.tileDot}
                      style={{ background: "#64748b" }}
                    />
                  </div>

                  {categories.map((cat) => {
                    const color = getCategoryColor(cat.nameRaw);
                    const icon = getCategoryIcon(cat);
                    const active = selectedCategory === cat.nameRaw;
                    return (
                      <div
                        key={cat._id}
                        className={`${styles.tile} ${active ? styles.tileSelected : ""}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleSelect(cat.nameRaw, null)}
                      >
                        <div
                          className={styles.tileIconWrap}
                          style={{ background: tint(color) }}
                        >
                          {icon ? (
                            <img src={icon} alt={cat.name} />
                          ) : (
                            <AppstoreOutlined style={{ color }} />
                          )}
                        </div>
                        <span className={styles.tileLabel}>{cat.name}</span>
                        <span
                          className={styles.tileDot}
                          style={{ background: color }}
                        />
                      </div>
                    );
                  })}

                  <div
                    className={`${styles.tile} ${selectedCategory === "OTROS" ? styles.tileSelected : ""}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleSelect("OTROS", null)}
                  >
                    <div
                      className={styles.tileIconWrap}
                      style={{ background: tint(getCategoryColor("OTROS")) }}
                    >
                      <FolderOpenOutlined
                        style={{ color: getCategoryColor("OTROS") }}
                      />
                    </div>
                    <span className={styles.tileLabel}>{otherLabel}</span>
                    <span
                      className={styles.tileDot}
                      style={{ background: getCategoryColor("OTROS") }}
                    />
                  </div>
                </div>

                <BrandThemeProvider>
                  <div className={styles.searchRow}>
                    <Input
                      className={styles.searchInput}
                      size="large"
                      allowClear
                      prefix={<SearchOutlined />}
                      placeholder={t("catalog.searchPlaceholder")}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </BrandThemeProvider>
              </div>

              {/* ===== Chip de filtro activo ===== */}
              {selectedCategory ? (
                <Flex
                  className={styles.selectedRow}
                  align="center"
                  gap={10}
                  wrap
                >
                  <Text type="secondary">{t("catalog.filteredBy")}</Text>
                  <Tag
                    className={styles.filterTag}
                    closable
                    onClose={(e) => {
                      e.preventDefault();
                      handleAllScenes();
                    }}
                  >
                    {selectedCategory === "OTROS"
                      ? otherLabel
                      : selectedCategory}
                  </Tag>
                  {selectedSubcategory ? (
                    <Tag
                      className={styles.filterTagSub}
                      closable
                      onClose={(e) => {
                        e.preventDefault();
                        handleClearSubcategory();
                      }}
                    >
                      {selectedSubcategory}
                    </Tag>
                  ) : null}
                </Flex>
              ) : null}

              {/* ===== Subcategorias en chips ===== */}
              {selectedCategory && selectedCategory !== "OTROS" ? (
                <div className={styles.subChips}>
                  {(selectedCategoryObj?.subcategories || []).map((sub) => (
                    <span
                      key={sub._id}
                      role="button"
                      tabIndex={0}
                      className={`${styles.subChip} ${
                        selectedSubcategory === sub.nameRaw
                          ? styles.subChipActive
                          : ""
                      }`}
                      onClick={() =>
                        handleSelect(selectedCategory, sub.nameRaw)
                      }
                    >
                      {sub.name}
                    </span>
                  ))}
                </div>
              ) : null}

              {/* ===== Grid de escenas (filtrado por el buscador) ===== */}
              {visibleScenes.length > 0 ? (
                <div className={styles.sceneGrid}>
                  {visibleScenes.map((scene) => (
                    <SalasItemSalas
                      key={scene.refId}
                      scene={scene}
                      teacher={teacher}
                      onUploadScene={handleUploadScene}
                    />
                  ))}
                </div>
              ) : (
                <div className={styles.empty}>
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      searchQuery
                        ? t("catalog.noResults")
                        : t("catalog.emptyCatalog")
                    }
                  />
                </div>
              )}
            </>
          ) : (
            <>
              {/* ===== Categorías (centradas y destacadas) ===== */}
              <div className={`${styles.categoriesBar} salas-list__buttons`}>
                <Button
                  size="large"
                  onClick={handleAllScenes}
                  className={`e-button-categories e-button-todas ${styles.catBtn} ${
                    !selectedCategory ? styles.isActive : ""
                  }`}
                >
                  {t("catalog.allButton")}
                </Button>

                {categories.map((cat) => (
                  <Button
                    size="large"
                    key={cat._id}
                    onClick={() => handleSelect(cat.nameRaw, null)}
                    className={`e-button-categories ${getCategoryClass(cat.nameRaw)} ${styles.catBtn} ${
                      selectedCategory === cat.nameRaw ? styles.isActive : ""
                    }`}
                  >
                    {cat.name}
                  </Button>
                ))}

                <Button
                  size="large"
                  onClick={() => handleSelect("OTROS", null)}
                  className={`e-button-categories ${getCategoryClass("OTROS")} ${styles.catBtn} ${
                    selectedCategory === "OTROS" ? styles.isActive : ""
                  }`}
                >
                  {otherLabel}
                </Button>
              </div>

              {/* ===== “Texto suelto” convertido en Chips bonitos ===== */}
              {selectedCategory ? (
                <Flex
                  className={styles.selectedRow}
                  align="center"
                  gap={10}
                  wrap
                >
                  <Text type="secondary">{t("catalog.filteredBy")}</Text>

                  <Tag
                    className={styles.filterTag}
                    closable
                    onClose={(e) => {
                      e.preventDefault();
                      handleAllScenes();
                    }}
                  >
                    {selectedCategory === "OTROS"
                      ? otherLabel
                      : selectedCategory}
                  </Tag>

                  {selectedSubcategory ? (
                    <Tag
                      className={styles.filterTagSub}
                      closable
                      onClose={(e) => {
                        e.preventDefault();
                        handleClearSubcategory();
                      }}
                    >
                      {selectedSubcategory}
                    </Tag>
                  ) : null}
                </Flex>
              ) : null}

              {/* ===== Subcategorías (más suaves) ===== */}
              {selectedCategory && selectedCategory !== "OTROS" ? (
                <div className={`${styles.subBar} salas-list__subcategories`}>
                  {(selectedCategoryObj?.subcategories || []).map((sub) => (
                    <Button
                      key={sub._id}
                      size="middle"
                      onClick={() =>
                        handleSelect(selectedCategory, sub.nameRaw)
                      }
                      className={`e-button-subcategories ${styles.subBtn} ${
                        selectedSubcategory === sub.nameRaw
                          ? styles.subActive
                          : ""
                      }`}
                    >
                      {String(sub.name).toUpperCase()}
                    </Button>
                  ))}
                </div>
              ) : null}

              {/* ===== Escenas (mantengo clases legacy de grid para no romper) ===== */}
              {scenes.length > 0 ? (
                <div
                  className={`salas-list__scenes ${scenes.length === 0 ? "empty" : ""}`}
                >
                  {scenes.map((scene) => (
                    <div
                      className="salas-list__scenes__salascolumn"
                      key={scene.refId}
                    >
                      <SalasItemSalas
                        scene={scene}
                        teacher={teacher}
                        onUploadScene={handleUploadScene}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.empty}>
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t("catalog.emptyCatalog")}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}

      {showRutasForm && (
        <RutasDidacticasForm
          accessToken={accessToken}
          classNumber={classNumber}
          teacher={teacher}
          scene={selectedScene}
          onClose={handleCloseRutasForm}
          reload={reload}
          onReload={onReload}
        />
      )}
    </div>
  );
}
